import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { useDebounce } from './useDebounce';

// minimal table-like shape accepted by this selector
export interface TableOption {
  id: number;
  tableName: string;
  tableType: string;
  zoneId: number;
  // allow extra fields from API types
  [key: string]: any;
}

interface TableSelectorProps {
  plantId?: number | null;
  zoneId?: number;
  value?: number | null; // tableId
  onChange: (tableId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
}

import { useInfiniteQuery } from '@tanstack/react-query';
import { getApiTables } from '@/api/gen/api';
import { PaginatedResponse } from '@/types';

interface UseInfiniteAutocompleteOptions<T> {
  queryKey: (search: string) => any[];
  queryFn: (params: {
    pageParam: number;
    search: string;
  }) => Promise<PaginatedResponse<T>>;
  search: string;
  debounceMs?: number;
}

export function useInfiniteAutocomplete<T>({
  queryKey,
  queryFn,
  search,
  debounceMs = 300,
}: UseInfiniteAutocompleteOptions<T>) {
  const debouncedSearch = useDebounce(search, debounceMs);

  return useInfiniteQuery({
    queryKey: queryKey(debouncedSearch),
    queryFn: ({ pageParam = 1 }) =>
      queryFn({ pageParam, search: debouncedSearch }),
    getNextPageParam: (lastPage: PaginatedResponse<T>, allPages) => {
      if (!lastPage) return undefined;
      const loaded = allPages.reduce(
        (acc, p) => acc + (p?.items?.length || 0),
        0
      );
      if (loaded < lastPage.totalCount) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });
}

export const useTablesInfiniteQuery = (
  plantId: number,
  search: string,
  zoneId?: number
) => {
  return useInfiniteAutocomplete({
    queryKey: debouncedSearch => [
      'tables',
      'infinite',
      plantId,
      zoneId,
      debouncedSearch,
    ],
    queryFn: ({ pageParam }) =>
      getApiTables({
        isPaginated: true,
        SearchString: search || undefined,
        PlantId: plantId,
        ZoneId: zoneId,
        page: pageParam as number,
        pageSize: 20,
      }).then((response: any) => response),
    search,
  });
};

export default function TableSelector({
  plantId,
  zoneId,
  value, // tableId
  onChange, // (tableId) => void
  placeholder = 'Selecciona una tabla',
  disabled = false,
  required = false,
  label = 'Tabla',
}: TableSelectorProps) {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Store previous plantId and zoneId to detect changes
  const prevPlantIdRef = useRef<number | null | undefined>(null);
  const prevZoneIdRef = useRef<number | undefined>(null);

  // Reset input value and selected value when plantId or zoneId changes to a different value
  useEffect(() => {
    const plantIdChanged = prevPlantIdRef.current !== plantId;
    const zoneIdChanged = prevZoneIdRef.current !== zoneId;

    if (plantIdChanged || zoneIdChanged) {
      setInputValue('');
      setSearchQuery('');
      onChange(null);
    }

    prevPlantIdRef.current = plantId;
    prevZoneIdRef.current = zoneId;
  }, [plantId, zoneId, onChange]);

  const effectivePlantId = zoneId ? null : plantId;

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTablesInfiniteQuery(effectivePlantId as any, debouncedSearch, zoneId);

  const options = useMemo(() => {
    const allOpts = (data?.pages?.flatMap(page => page?.items || []) ||
      []) as TableOption[];
    // Filter by zoneId if provided
    const filteredOpts = zoneId
      ? allOpts.filter(table => table.zoneId === zoneId)
      : allOpts;
    // Ensure uniqueness by id
    const uniqueOpts = [
      ...new Map(filteredOpts.map(opt => [opt.id, opt])).values(),
    ];
    // Always include the selected table if it exists and is not already in the list
    const selectedTable = allOpts.find(table => table.id === value);
    if (value && selectedTable && !uniqueOpts.some(opt => opt.id === value)) {
      uniqueOpts.unshift(selectedTable);
    }
    return uniqueOpts;
  }, [data, zoneId, value]);

  // Find the selected table based on id
  const selectedTable = useMemo(() => {
    return options.find(table => table.id === value) || null;
  }, [options, value]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 50 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <Autocomplete
      disablePortal
      freeSolo
      value={selectedTable}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          onChange(newValue.id);
        } else {
          // If string or null, do nothing or deselect if needed
          onChange(null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') {
          setSearchQuery(newInputValue);
          if (value) {
            onChange(null);
          }
        }
      }}
      options={options}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.tableName
      }
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={isLoading}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={error ? 'Error cargando tablas' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      noOptionsText={isLoading ? 'Cargando...' : 'No hay opciones'}
    />
  );
}
